import React, { useRef, useEffect, useState, useCallback } from 'react';
import { drawElevation } from '../../utils/drawElevation';
import { Button } from '../ui';
import { RoofEditor } from '../editors/RoofEditor';
import { PostEditor } from '../editors/PostEditor';
import { DoorEditor, WindowEditor } from '../editors/DoorWindowEditor';
import { DimensionIcon } from '../toolbar/ToolbarIcons';
import { GRID_SIZE } from '../../constants/grid';

// Inline SVG icons for elevation toolbar
const DimsIcon = ({ color = '#6080a0', size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Dimension line with hash marks */}
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="8" x2="4" y2="16" />
    <line x1="20" y1="8" x2="20" y2="16" />
    {/* Arrows */}
    <path d="M4 12l3-2M4 12l3 2" />
    <path d="M20 12l-3-2M20 12l-3 2" />
  </svg>
);

const MeasureIcon = ({ color = '#6080a0', size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Ruler with marks */}
    <rect x="2" y="6" width="20" height="12" rx="1" fill={color} fillOpacity="0.1" />
    <rect x="2" y="6" width="20" height="12" rx="1" />
    {/* Measurement marks */}
    <line x1="6" y1="6" x2="6" y2="10" />
    <line x1="10" y1="6" x2="10" y2="12" />
    <line x1="14" y1="6" x2="14" y2="10" />
    <line x1="18" y1="6" x2="18" y2="12" />
  </svg>
);

const AddDimensionIcon = ({ color = '#6080a0', size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Dimension line */}
    <line x1="2" y1="14" x2="16" y2="14" />
    <line x1="2" y1="10" x2="2" y2="18" />
    <line x1="16" y1="10" x2="16" y2="18" />
    {/* Plus sign */}
    <circle cx="19" cy="7" r="4" fill={color} fillOpacity="0.15" />
    <line x1="19" y1="5" x2="19" y2="9" />
    <line x1="17" y1="7" x2="21" y2="7" />
  </svg>
);

const LevelsIcon = ({ color = '#6080a0', size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Horizontal level lines */}
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
    {/* Vertical tick marks */}
    <line x1="3" y1="4" x2="3" y2="8" />
    <line x1="3" y1="10" x2="3" y2="14" />
    <line x1="3" y1="16" x2="3" y2="20" />
  </svg>
);

const WindowIcon = ({ color = '#6080a0', size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="1" />
    <line x1="12" y1="4" x2="12" y2="20" />
    <line x1="4" y1="12" x2="20" y2="12" />
  </svg>
);

const DoorIcon = ({ color = '#6080a0', size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="3" width="12" height="18" rx="1" />
    <circle cx="15" cy="12" r="1.5" fill={color} />
  </svg>
);

/**
 * ElevationView - Advanced 2D elevation views using shared drawElevation utility
 * Features: Siding patterns, scale control, dimensions, measure tool, level markers
 */

// Siding pattern options
const SIDING_OPTIONS = [
  { value: 'horizontal', label: 'Lap' },
  { value: 'board-batten', label: 'Board & Batten' },
  { value: 'shingle', label: 'Shingle' },
];

// Foundation block size options (inches)
const BLOCK_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 8, label: '8"' },
  { value: 12, label: '12"' },
  { value: 16, label: '16"' },
  { value: 24, label: '24"' },
];

// Scale options
const SCALE_OPTIONS = [
  { value: 'auto', label: 'Auto Fit' },
  { value: '1/16', label: '1/16" = 1\'-0"' },
  { value: '1/8', label: '1/8" = 1\'-0"' },
  { value: '3/16', label: '3/16" = 1\'-0"' },
  { value: '1/4', label: '1/4" = 1\'-0"' },
  { value: '3/8', label: '3/8" = 1\'-0"' },
  { value: '1/2', label: '1/2" = 1\'-0"' },
  { value: '3/4', label: '3/4" = 1\'-0"' },
  { value: '1', label: '1" = 1\'-0"' },
];

// Scale factors map (pixels per inch) - must match drawElevation.js
const SCALE_FACTORS = {
  '1/16': (0.0625 / 12) * 96,  // 0.5 pixels per inch
  '1/8': (0.125 / 12) * 96,    // 1 pixel per inch
  '3/16': (0.1875 / 12) * 96,  // 1.5 pixels per inch
  '1/4': (0.25 / 12) * 96,     // 2 pixels per inch
  '3/8': (0.375 / 12) * 96,    // 3 pixels per inch
  '1/2': (0.5 / 12) * 96,      // 4 pixels per inch
  '3/4': (0.75 / 12) * 96,     // 6 pixels per inch
  '1': (1 / 12) * 96,          // 8 pixels per inch
};

const ElevationView = ({
  activeFloor,
  floors = [],
  isMobile = false,
  onClose,
  onUpdateFloor,
  onUpdateRoof,
  onRemoveRoof,
  onUpdatePost,
  onRemovePost,
  onUpdateWallHeight,
  onUpdateFloorElevation,
  onAddWindow,
  onAddDoor,
  onUpdateWindow,
  onUpdateDoor,
  onDeleteWindow,
  onDeleteDoor,
  activeFloorId,
  onSetActiveFloorId,
  elevationSettings,
  onUpdateElevationSettings,
  formatMeasurement,
  units = 'imperial',
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // View state - initialize from stored settings if available
  const [activeElevation, setActiveElevation] = useState(elevationSettings?.activeElevation || 'north');
  const [renderMode, setRenderMode] = useState(elevationSettings?.renderMode || 'color');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Options state - initialize from stored settings if available
  const [sidingPattern, setSidingPattern] = useState(elevationSettings?.sidingPattern || 'horizontal');
  const [foundationHeight, setFoundationHeight] = useState(elevationSettings?.foundationHeight ?? 12);
  const [showDimensions, setShowDimensions] = useState(elevationSettings?.showDimensions ?? false);
  const [selectedScale, setSelectedScale] = useState(elevationSettings?.selectedScale || 'auto');
  const [showLevelLines, setShowLevelLines] = useState(elevationSettings?.showLevelLines ?? true);

  // Measure tool state
  const [measureMode, setMeasureMode] = useState(false);
  const [measureStart, setMeasureStart] = useState(null);
  const [measureEnd, setMeasureEnd] = useState(null);

  // Dimension tool state
  const [dimensionMode, setDimensionMode] = useState(false);
  const [dimensionStart, setDimensionStart] = useState(null);
  const [dimensionPreview, setDimensionPreview] = useState(null);
  // Dimensions stored per elevation direction
  const [elevationDimensions, setElevationDimensions] = useState(
    elevationSettings?.dimensions || { north: [], south: [], east: [], west: [] }
  );
  const [selectedDimensionId, setSelectedDimensionId] = useState(null);

  // Save settings when they change
  useEffect(() => {
    if (onUpdateElevationSettings) {
      onUpdateElevationSettings({
        activeElevation,
        renderMode,
        sidingPattern,
        foundationHeight,
        showDimensions,
        selectedScale,
        showLevelLines,
        dimensions: elevationDimensions,
      });
    }
  }, [activeElevation, renderMode, sidingPattern, foundationHeight, showDimensions, selectedScale, showLevelLines, elevationDimensions, onUpdateElevationSettings]);

  // Selection state for interactive elements
  const [selectedRoofId, setSelectedRoofId] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const [editingWallHeight, setEditingWallHeight] = useState(false);
  const [wallHeightInput, setWallHeightInput] = useState('');
  const [editingRoof, setEditingRoof] = useState(false); // Show roof editor panel

  // Level line extension state (how far each level line extends to the right)
  // Keys: 'grade', 'level1', 'level2' - values are pixel extensions from default
  const [levelLineExtensions, setLevelLineExtensions] = useState({
    grade: 0,
    level1: 0,
    level2: 0,
  });

  // Dragging state for level line grip points
  const [draggingLevelGrip, setDraggingLevelGrip] = useState(null); // { id: 'level1', startX, startExtension }
  const [hoveredGrip, setHoveredGrip] = useState(null); // id of hovered grip point

  // Render info from drawElevation (for hit testing)
  const [renderInfo, setRenderInfo] = useState(null);

  // Floor management panel state
  const [showFloorsPanel, setShowFloorsPanel] = useState(false);
  const [editingFloorElevation, setEditingFloorElevation] = useState(null); // { floorId, value }

  // Placement mode state for adding windows/doors in elevation view
  const [placementMode, setPlacementMode] = useState(null); // 'window' | 'door' | null
  const [placementPreview, setPlacementPreview] = useState(null); // { x, y, width, height }

  // Selection state for windows/doors in elevation view
  const [selectedWindowId, setSelectedWindowId] = useState(null);
  const [selectedDoorId, setSelectedDoorId] = useState(null);
  const [selectedItemFloorId, setSelectedItemFloorId] = useState(null); // Track which floor the selected item is on

  // Dragging state for windows/doors
  const [draggingWindowDoor, setDraggingWindowDoor] = useState(null); // { type: 'window'|'door', id, startX, startY, originalPosition, originalSillHeight }

  // Clear selection when elevation view changes (selected item may not be visible in new view)
  useEffect(() => {
    setSelectedWindowId(null);
    setSelectedDoorId(null);
    setSelectedItemFloorId(null);
  }, [activeElevation]);

  // Keyboard handling for nudging selected windows/doors with arrow keys
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle arrow keys when a window or door is selected
      if (!selectedWindowId && !selectedDoorId) return;
      if (!selectedItemFloorId) return;

      // Check for left/right arrow keys
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      e.preventDefault();

      // Get the floor and selected item
      const floor = floors.find(f => f.id === selectedItemFloorId);
      if (!floor) return;

      const isHorizontal = activeElevation === 'north' || activeElevation === 'south';

      // Nudge amount: 0.01 = 1% of wall length, or 0.005 for fine control with Shift
      const nudgeAmount = e.shiftKey ? 0.005 : 0.01;

      // For south and west elevations, the view is flipped (flipX = true)
      const flipX = renderInfo?.flipX || false;
      const baseDirection = e.key === 'ArrowRight' ? 1 : -1;

      if (selectedWindowId && onUpdateWindow) {
        const win = floor.windows?.find(w => w.id === selectedWindowId);
        if (!win) return;
        const wall = floor.walls?.find(w => w.id === win.wallId);
        if (!wall) return;

        // Calculate wall direction to handle walls running either way
        const wallDiff = isHorizontal
          ? (wall.end.x - wall.start.x)
          : (wall.end.y - wall.start.y);
        const wallDirectionSign = wallDiff >= 0 ? 1 : -1;

        // Combine flip and wall direction for correct visual movement
        const direction = baseDirection * (flipX ? -1 : 1) * wallDirectionSign;

        const currentPosition = win.position || 0.5;
        const newPosition = Math.max(0.05, Math.min(0.95, currentPosition + (direction * nudgeAmount)));
        onUpdateWindow(selectedItemFloorId, selectedWindowId, { position: newPosition });
      } else if (selectedDoorId && onUpdateDoor) {
        const door = floor.doors?.find(d => d.id === selectedDoorId);
        if (!door) return;
        const wall = floor.walls?.find(w => w.id === door.wallId);
        if (!wall) return;

        // Calculate wall direction to handle walls running either way
        const wallDiff = isHorizontal
          ? (wall.end.x - wall.start.x)
          : (wall.end.y - wall.start.y);
        const wallDirectionSign = wallDiff >= 0 ? 1 : -1;

        // Combine flip and wall direction for correct visual movement
        const direction = baseDirection * (flipX ? -1 : 1) * wallDirectionSign;

        const currentPosition = door.position || 0.5;
        const newPosition = Math.max(0.05, Math.min(0.95, currentPosition + (direction * nudgeAmount)));
        onUpdateDoor(selectedItemFloorId, selectedDoorId, { position: newPosition });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedWindowId, selectedDoorId, selectedItemFloorId, floors, onUpdateWindow, onUpdateDoor, renderInfo, activeElevation]);

  // Container dimensions (actual available space)
  const [containerSize, setContainerSize] = useState({ width: 1200, height: 700 });
  // Canvas dimensions - may be larger than container when using fixed scale
  const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 700 });

  // Measure container size for responsive canvas
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Account for padding
        const padding = isMobile ? 20 : 40;
        const newContainerSize = {
          width: Math.max(300, rect.width - padding),
          height: Math.max(200, rect.height - padding),
        };
        setContainerSize(newContainerSize);
        // Only auto-set canvas size if not using fixed scale
        // (fixed scale canvas sizing is handled in the render effect)
        if (selectedScale === 'auto') {
          setCanvasSize(newContainerSize);
        }
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    // Small delay to ensure container is rendered
    const timer = setTimeout(updateSize, 100);

    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timer);
    };
  }, [isMobile, selectedScale]);

  // Calculate required canvas size when using fixed scale
  useEffect(() => {
    if (selectedScale === 'auto') {
      // Auto scale - canvas matches container
      setCanvasSize(containerSize);
      return;
    }

    // For fixed scale, calculate required canvas size based on model dimensions
    if (renderInfo && renderInfo.modelWidthInches && renderInfo.modelHeightInches) {
      const scaleFactor = SCALE_FACTORS[selectedScale];
      if (scaleFactor) {
        // Add margins (60px on each side horizontally, 80px vertically)
        const marginX = 60;
        const marginY = 80;
        // Add space for level lines on right
        const levelLineSpace = 150;

        const requiredWidth = renderInfo.modelWidthInches * scaleFactor + marginX * 2 + levelLineSpace;
        const requiredHeight = renderInfo.modelHeightInches * scaleFactor + marginY * 2;

        // Use the larger of container size or required size
        setCanvasSize({
          width: Math.max(containerSize.width, requiredWidth),
          height: Math.max(containerSize.height, requiredHeight),
        });
      }
    }
  }, [selectedScale, containerSize, renderInfo]);

  // Fit to screen
  const fitToScreen = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Use ref to track isPanning for touch handlers (avoids stale closure issues)
  const isPanningRef = useRef(false);

  // Pan handlers
  const handlePanStart = useCallback((clientX, clientY) => {
    if (measureMode) return;
    setIsPanning(true);
    isPanningRef.current = true;
    setPanStart({ x: clientX - pan.x, y: clientY - pan.y });
  }, [pan, measureMode]);

  const handlePanMove = useCallback((clientX, clientY) => {
    if (!isPanning) return;
    setPan({
      x: clientX - panStart.x,
      y: clientY - panStart.y,
    });
  }, [isPanning, panStart]);

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
    isPanningRef.current = false;
  }, []);

  // Convert screen coordinates to model coordinates (inches from building origin)
  // Building origin is at (offsetX, gradeY) in screen space
  const screenToModel = useCallback((screenX, screenY) => {
    if (!renderInfo || !renderInfo.elevScale || !renderInfo.offsetX || !renderInfo.gradeY) {
      return null;
    }
    const { elevScale, offsetX, gradeY } = renderInfo;
    // X: distance from left edge of building in inches
    // Y: height above grade in inches (positive up)
    return {
      x: (screenX - offsetX) / elevScale,
      y: (gradeY - screenY) / elevScale, // Y increases upward in model space
    };
  }, [renderInfo]);

  // Convert model coordinates back to screen coordinates
  const modelToScreen = useCallback((modelX, modelY) => {
    if (!renderInfo || !renderInfo.elevScale || !renderInfo.offsetX || !renderInfo.gradeY) {
      return null;
    }
    const { elevScale, offsetX, gradeY } = renderInfo;
    return {
      x: offsetX + modelX * elevScale,
      y: gradeY - modelY * elevScale, // Y increases downward in screen space
    };
  }, [renderInfo]);

  // Get snap points in screen coordinates based on current render info
  const getSnapPoints = useCallback(() => {
    if (!renderInfo) return [];
    const { elevScale, offsetX, gradeY, roofBaseY, groundY, elevWidth, drawWallHeight, drawFoundationHeight, drawRoofPeak, drawOverhang } = renderInfo;
    const snapPoints = [];
    const SNAP_THRESHOLD = 12; // pixels

    // Building corners (left and right edges at various heights)
    const leftX = offsetX;
    const rightX = offsetX + elevWidth;

    // Grade level (0)
    snapPoints.push({ x: leftX, y: gradeY, label: 'Grade Left' });
    snapPoints.push({ x: rightX, y: gradeY, label: 'Grade Right' });

    // Foundation bottom (if exists)
    if (groundY && groundY !== gradeY) {
      snapPoints.push({ x: leftX, y: groundY, label: 'Ground Left' });
      snapPoints.push({ x: rightX, y: groundY, label: 'Ground Right' });
    }

    // Top of wall / eave level
    if (roofBaseY) {
      snapPoints.push({ x: leftX, y: roofBaseY, label: 'Eave Left' });
      snapPoints.push({ x: rightX, y: roofBaseY, label: 'Eave Right' });

      // Roof peak (center of building at peak height)
      if (drawRoofPeak) {
        const peakY = roofBaseY - drawRoofPeak;
        const centerX = offsetX + elevWidth / 2;
        snapPoints.push({ x: centerX, y: peakY, label: 'Ridge' });

        // Overhang corners
        if (drawOverhang) {
          snapPoints.push({ x: leftX - drawOverhang, y: roofBaseY, label: 'Overhang Left' });
          snapPoints.push({ x: rightX + drawOverhang, y: roofBaseY, label: 'Overhang Right' });
        }
      }
    }

    // Level line snap points
    if (renderInfo.levelBounds) {
      renderInfo.levelBounds.forEach(level => {
        snapPoints.push({ x: leftX, y: level.bounds.y, label: level.id });
        snapPoints.push({ x: rightX, y: level.bounds.y, label: level.id });
      });
    }

    return snapPoints;
  }, [renderInfo]);

  // Snap a position to nearby snap points
  const getSnappedPosition = useCallback((x, y) => {
    const snapPoints = getSnapPoints();
    const SNAP_THRESHOLD = 12; // pixels

    let snappedX = x;
    let snappedY = y;
    let snapFoundX = false;
    let snapFoundY = false;

    // Check for X snap (vertical alignment)
    for (const sp of snapPoints) {
      if (Math.abs(x - sp.x) < SNAP_THRESHOLD) {
        snappedX = sp.x;
        snapFoundX = true;
        break;
      }
    }

    // Check for Y snap (horizontal alignment)
    for (const sp of snapPoints) {
      if (Math.abs(y - sp.y) < SNAP_THRESHOLD) {
        snappedY = sp.y;
        snapFoundY = true;
        break;
      }
    }

    return { x: snappedX, y: snappedY, snappedX: snapFoundX, snappedY: snapFoundY };
  }, [getSnapPoints]);

  // Check if click hits a roof (iterate through all roofs, check in reverse order so topmost drawn roof is hit first)
  const checkRoofHit = (x, y) => {
    if (!renderInfo || !renderInfo.roofBounds || !Array.isArray(renderInfo.roofBounds)) return null;

    // Check in reverse order - roofs drawn later (on top) should be hit first
    for (let i = renderInfo.roofBounds.length - 1; i >= 0; i--) {
      const roofBound = renderInfo.roofBounds[i];
      const { bounds, id } = roofBound;
      if (x >= bounds.x && x <= bounds.x + bounds.width &&
          y >= bounds.y && y <= bounds.y + bounds.height) {
        return { id, bounds };
      }
    }
    return null;
  };

  // Check if click hits a post. Posts draw in front of the walls and roofs they
  // stand before, so they are tested ahead of both.
  const checkPostHit = (x, y) => {
    if (!renderInfo || !Array.isArray(renderInfo.postBounds)) return null;

    for (let i = renderInfo.postBounds.length - 1; i >= 0; i--) {
      const { bounds, id, floorId } = renderInfo.postBounds[i];
      if (x >= bounds.x && x <= bounds.x + bounds.width &&
          y >= bounds.y && y <= bounds.y + bounds.height) {
        return { id, floorId };
      }
    }
    return null;
  };

  // Check if click hits a window or door in elevation view
  const checkWindowDoorHit = useCallback((clickX, clickY) => {
    if (!renderInfo) return null;

    const { elevScale, offsetX, floorY, flipX, gradeY, elevationWalls } = renderInfo;
    // Openings are drawn from the roof-expanded origin, so hit-test against that
    const minPos = renderInfo.modelMinPos ?? renderInfo.minPos;
    const maxPos = renderInfo.modelMaxPos ?? renderInfo.maxPos;
    const isHorizontal = activeElevation === 'north' || activeElevation === 'south';
    // GRID_SIZE imported from constants/grid

    // Get the IDs of walls that are actually visible in this elevation view
    const visibleWallIds = new Set((elevationWalls || []).map(w => w.id));

    const floorsToCheck = floors.length > 0 ? floors : (activeFloor ? [activeFloor] : []);

    // Check windows first (they're typically smaller/more precise targets)
    for (const floor of floorsToCheck) {
      const floorElev = floor.floorElevation || 0;
      const walls = floor.walls || [];
      const windows = floor.windows || [];
      const doors = floor.doors || [];

      // Check windows
      for (const win of windows) {
        const wall = walls.find(w => w.id === win.wallId);
        if (!wall) continue;

        // Only check windows on walls that are visible in this elevation
        if (!visibleWallIds.has(wall.id)) continue;

        // Check wall orientation
        const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
        const isHorizontalWall = Math.abs(Math.cos(wallAngle)) > 0.7;
        if (isHorizontal !== isHorizontalWall) continue;

        // Calculate window position in model coordinates
        let winPos;
        if (isHorizontal) {
          winPos = wall.start.x + (wall.end.x - wall.start.x) * win.position;
        } else {
          winPos = wall.start.y + (wall.end.y - wall.start.y) * win.position;
        }

        // Convert to screen coordinates
        let winCenterX;
        if (flipX) {
          winCenterX = offsetX + (maxPos - winPos) * (6 / GRID_SIZE) * elevScale;
        } else {
          winCenterX = offsetX + (winPos - minPos) * (6 / GRID_SIZE) * elevScale;
        }

        const winW = (win.width || 36) * elevScale;
        const winH = (win.height || 48) * elevScale;
        const winSill = (win.sillHeight || 36) * elevScale;
        const winFloorOffset = floorElev * elevScale;
        const winY = floorY - winSill - winH - winFloorOffset;
        const winX = winCenterX - winW / 2;

        // Hit test
        if (clickX >= winX - 5 && clickX <= winX + winW + 5 &&
            clickY >= winY - 5 && clickY <= winY + winH + 5) {
          return { type: 'window', id: win.id, floorId: floor.id, item: win };
        }
      }

      // Check doors
      for (const door of doors) {
        const wall = walls.find(w => w.id === door.wallId);
        if (!wall) continue;

        // Only check doors on walls that are visible in this elevation
        if (!visibleWallIds.has(wall.id)) continue;

        // Check wall orientation
        const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
        const isHorizontalWall = Math.abs(Math.cos(wallAngle)) > 0.7;
        if (isHorizontal !== isHorizontalWall) continue;

        // Calculate door position
        let doorPos;
        if (isHorizontal) {
          doorPos = wall.start.x + (wall.end.x - wall.start.x) * door.position;
        } else {
          doorPos = wall.start.y + (wall.end.y - wall.start.y) * door.position;
        }

        // Convert to screen coordinates
        let doorX;
        if (flipX) {
          doorX = offsetX + (maxPos - doorPos) * (6 / GRID_SIZE) * elevScale;
        } else {
          doorX = offsetX + (doorPos - minPos) * (6 / GRID_SIZE) * elevScale;
        }

        const doorW = (door.width || 36) * elevScale;
        const doorH = (door.height || 84) * elevScale;
        const doorFloorOffset = floorElev * elevScale;
        const doorY = floorY - doorH - doorFloorOffset;

        // Hit test (door is centered on doorX)
        if (clickX >= doorX - doorW / 2 - 5 && clickX <= doorX + doorW / 2 + 5 &&
            clickY >= doorY - 5 && clickY <= doorY + doorH + 5) {
          return { type: 'door', id: door.id, floorId: floor.id, item: door };
        }
      }
    }

    return null;
  }, [renderInfo, activeElevation, floors, activeFloor]);

  // Check if click hits a dimension (dimensions are stored in model coords)
  const checkDimensionHit = (x, y) => {
    const currentDimensions = elevationDimensions[activeElevation] || [];
    const hitThreshold = 10; // pixels

    for (const dim of currentDimensions) {
      // Convert model coords to screen coords for hit testing
      const startScreen = modelToScreen(dim.start.x, dim.start.y);
      const endScreen = modelToScreen(dim.end.x, dim.end.y);
      if (!startScreen || !endScreen) continue;

      const dx = endScreen.x - startScreen.x;
      const dy = endScreen.y - startScreen.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length < 5) continue;

      // Perpendicular offset (in pixels)
      const perpX = -dy / length;
      const perpY = dx / length;
      const offset = dim.offset || 30;

      // Dimension line endpoints in screen coords
      const dimStart = { x: startScreen.x + perpX * offset, y: startScreen.y + perpY * offset };
      const dimEnd = { x: endScreen.x + perpX * offset, y: endScreen.y + perpY * offset };

      // Check distance from point to line segment
      const distToLine = pointToSegmentDistance(x, y, dimStart.x, dimStart.y, dimEnd.x, dimEnd.y);
      if (distToLine < hitThreshold) {
        return dim;
      }
    }
    return null;
  };

  // Helper: distance from point to line segment
  const pointToSegmentDistance = (px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;
    if (lengthSq === 0) return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);

    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t));

    const nearestX = x1 + t * dx;
    const nearestY = y1 + t * dy;
    return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
  };

  // Check if click hits a level line
  const checkLevelLineHit = (x, y) => {
    if (!renderInfo || !renderInfo.levelBounds) return null;

    for (const level of renderInfo.levelBounds) {
      const bounds = level.bounds;
      // Check if click is near the level line (within 15 pixels vertically)
      // The bounds structure has: x, y, width, height
      if (Math.abs(y - bounds.y) < 20 &&
          x >= bounds.x - 50 &&
          x <= bounds.x + bounds.width + 100) {
        return level;
      }
    }
    return null;
  };

  // Check if position hits a level line grip point (the end of the level line)
  const checkGripPointHit = (x, y) => {
    if (!renderInfo || !renderInfo.levelBounds) return null;

    for (const level of renderInfo.levelBounds) {
      // Use the gripX/gripY calculated by drawElevation (includes extensions)
      const gripX = level.gripX;
      const gripY = level.gripY;

      // Check if within 15 pixels of grip point
      const dist = Math.sqrt((x - gripX) ** 2 + (y - gripY) ** 2);
      if (dist < 15) {
        return level;
      }
    }
    return null;
  };

  // Snap extension value to nearby level line extensions
  const snapExtension = (extension, excludeId) => {
    const SNAP_THRESHOLD = 10; // pixels
    const otherExtensions = Object.entries(levelLineExtensions)
      .filter(([id]) => id !== excludeId)
      .map(([, ext]) => ext);

    for (const otherExt of otherExtensions) {
      if (Math.abs(extension - otherExt) < SNAP_THRESHOLD) {
        return otherExt;
      }
    }
    // Also snap to 0 (default position)
    if (Math.abs(extension) < SNAP_THRESHOLD) {
      return 0;
    }
    return extension;
  };

  // Update wall height
  const updateWallHeight = (newHeightInches) => {
    // Use onUpdateWallHeight prop if available (preferred - updates model space)
    if (onUpdateWallHeight) {
      onUpdateWallHeight(newHeightInches);
      setEditingWallHeight(false);
      return;
    }

    // Fallback to onUpdateFloor if available
    if (!activeFloor || !onUpdateFloor) return;

    const updatedWalls = activeFloor.walls.map(wall => ({
      ...wall,
      height: newHeightInches,
    }));

    onUpdateFloor({
      ...activeFloor,
      walls: updatedWalls,
    });

    setEditingWallHeight(false);
  };

  // Get selected roof object
  const selectedPost = selectedPostId
    ? (floors.find(f => f.id === selectedItemFloorId) || activeFloor)?.posts?.find(p => p.id === selectedPostId)
    : null;

  const handlePostUpdate = useCallback((updates) => {
    if (!selectedPostId) return;
    if (onUpdatePost) {
      onUpdatePost(selectedPostId, updates);
    } else if (onUpdateFloor && activeFloor) {
      onUpdateFloor({
        ...activeFloor,
        posts: (activeFloor.posts || []).map(p => p.id === selectedPostId ? { ...p, ...updates } : p),
      });
    }
  }, [selectedPostId, onUpdatePost, onUpdateFloor, activeFloor]);

  const handlePostDelete = useCallback(() => {
    if (!selectedPostId) return;
    if (onRemovePost) {
      onRemovePost(selectedPostId);
    } else if (onUpdateFloor && activeFloor) {
      onUpdateFloor({
        ...activeFloor,
        posts: (activeFloor.posts || []).filter(p => p.id !== selectedPostId),
      });
    }
    setSelectedPostId(null);
  }, [selectedPostId, onRemovePost, onUpdateFloor, activeFloor]);

  const selectedRoof = selectedRoofId
    ? activeFloor?.roofs?.find(r => r.id === selectedRoofId)
    : null;

  // Update roof properties
  const handleRoofUpdate = (updates) => {
    if (!selectedRoofId) return;

    // Use onUpdateRoof if available (from App.jsx), otherwise fall back to onUpdateFloor
    if (onUpdateRoof) {
      onUpdateRoof(selectedRoofId, updates);
    } else if (onUpdateFloor && activeFloor) {
      const updatedRoofs = activeFloor.roofs.map(roof =>
        roof.id === selectedRoofId ? { ...roof, ...updates } : roof
      );
      onUpdateFloor({
        ...activeFloor,
        roofs: updatedRoofs,
      });
    }
  };

  // Delete roof
  const handleRoofDelete = () => {
    if (!selectedRoofId) return;

    // Use onRemoveRoof if available, otherwise fall back to onUpdateFloor
    if (onRemoveRoof) {
      onRemoveRoof(selectedRoofId);
    } else if (onUpdateFloor && activeFloor) {
      const updatedRoofs = activeFloor.roofs.filter(roof => roof.id !== selectedRoofId);
      onUpdateFloor({
        ...activeFloor,
        roofs: updatedRoofs,
      });
    }

    setSelectedRoofId(null);
    setEditingRoof(false);
  };

  // Convert screen coordinates to logical canvas coordinates
  const screenToCanvas = useCallback((clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return { x: 0, y: 0 };

    const containerRect = container.getBoundingClientRect();
    // Get center of container
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;

    // Mouse position relative to container
    const mouseX = clientX - containerRect.left;
    const mouseY = clientY - containerRect.top;

    // Account for pan (pan is applied to canvas center)
    // and calculate position in logical canvas coordinates
    const canvasDisplayWidth = canvasSize.width * zoom;
    const canvasDisplayHeight = canvasSize.height * zoom;

    // Canvas position relative to container center
    const canvasLeft = containerCenterX - canvasDisplayWidth / 2 + pan.x;
    const canvasTop = containerCenterY - canvasDisplayHeight / 2 + pan.y;

    // Convert to logical coordinates
    const x = (mouseX - canvasLeft) / zoom;
    const y = (mouseY - canvasTop) / zoom;

    return { x, y };
  }, [pan, zoom, canvasSize]);

  // Mouse handlers
  const handleMouseDown = (e) => {
    const { x, y } = screenToCanvas(e.clientX, e.clientY);

    // Check for grip point hit first (highest priority)
    const hitGrip = checkGripPointHit(x, y);
    if (hitGrip && !measureMode && !editingWallHeight) {
      e.preventDefault();
      setDraggingLevelGrip({
        id: hitGrip.id,
        startX: x,
        startExtension: levelLineExtensions[hitGrip.id] || 0,
      });
      return;
    }

    // Check for dragging a selected window/door
    if ((selectedWindowId || selectedDoorId) && !measureMode && !dimensionMode && !placementMode) {
      const hitWindowDoor = checkWindowDoorHit(x, y);
      if (hitWindowDoor) {
        // Check if we're clicking on the currently selected item
        if ((hitWindowDoor.type === 'window' && hitWindowDoor.id === selectedWindowId) ||
            (hitWindowDoor.type === 'door' && hitWindowDoor.id === selectedDoorId)) {
          e.preventDefault();
          const floor = floors.find(f => f.id === selectedItemFloorId) || activeFloor;
          let originalItem;
          if (hitWindowDoor.type === 'window') {
            originalItem = floor?.windows?.find(w => w.id === selectedWindowId);
          } else {
            originalItem = floor?.doors?.find(d => d.id === selectedDoorId);
          }
          if (originalItem) {
            setDraggingWindowDoor({
              type: hitWindowDoor.type,
              id: hitWindowDoor.id,
              floorId: hitWindowDoor.floorId,
              startX: x,
              startY: y,
              originalPosition: originalItem.position,
              originalSillHeight: originalItem.sillHeight || 36, // Only for windows
            });
            return;
          }
        }
      }
    }

    if (measureMode) {
      setMeasureStart({ x, y });
      setMeasureEnd({ x, y });
    } else if (dimensionMode) {
      // Apply snapping to dimension start point
      const snappedPos = getSnappedPosition(x, y);
      setDimensionStart(snappedPos);
      setDimensionPreview(snappedPos);
    } else {
      handlePanStart(e.clientX, e.clientY);
    }
  };

  // Handle click for level lines, roofs, dimensions, and placement
  const handleCanvasClick = useCallback((e) => {
    // Use ref for isPanning check to avoid stale closure issues with touch handlers
    if (measureMode || dimensionMode || isPanningRef.current || draggingLevelGrip || draggingWindowDoor) return;
    // Don't process if clicking inside a popup
    if (editingWallHeight || editingRoof) return;

    const { x, y } = screenToCanvas(e.clientX, e.clientY);

    // Handle placement mode (adding windows/doors)
    if (placementMode && renderInfo) {
      const { elevScale, offsetX, floorY, elevWidth, flipX, gradeY } = renderInfo;
      const minPos = renderInfo.modelMinPos ?? renderInfo.minPos;
      const maxPos = renderInfo.modelMaxPos ?? renderInfo.maxPos;
      const isHorizontal = activeElevation === 'north' || activeElevation === 'south';
      // GRID_SIZE imported from constants/grid

      // Check if click is within the elevation drawing area
      if (x >= offsetX && x <= offsetX + elevWidth) {
        // Calculate position along the elevation (0 to elevWidth)
        const clickPosAlongElev = x - offsetX;

        // Convert screen position back to model position
        // Formula derived from: screenX = offsetX + (modelPos - minPos) * (6 / GRID_SIZE) * elevScale
        let modelPos;
        if (flipX) {
          modelPos = maxPos - (clickPosAlongElev / elevScale) * (GRID_SIZE / 6);
        } else {
          modelPos = minPos + (clickPosAlongElev / elevScale) * (GRID_SIZE / 6);
        }

        // Calculate click height from grade (ground level)
        const clickHeightFromGrade = (gradeY - y) / elevScale; // in inches

        // Search ALL floors for walls at this position, not just active floor
        let targetWall = null;
        let targetFloor = null;
        let wallPosition = 0.5;
        let bestMatch = null;

        const floorsToSearch = floors.length > 0 ? floors : (activeFloor ? [activeFloor] : []);

        for (const floor of floorsToSearch) {
          const floorElev = floor.floorElevation || 0;
          const walls = (floor.walls || []).filter(w => w.type === 'exterior' || w.type?.startsWith('post-') || w.type === 'exterior-2x4');

          for (const wall of walls) {
            const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
            const isHorizontalWall = Math.abs(Math.cos(wallAngle)) > 0.7;

            // Skip walls that don't match the elevation orientation
            if (isHorizontal !== isHorizontalWall) continue;

            const wallStart = isHorizontal ? Math.min(wall.start.x, wall.end.x) : Math.min(wall.start.y, wall.end.y);
            const wallEnd = isHorizontal ? Math.max(wall.start.x, wall.end.x) : Math.max(wall.start.y, wall.end.y);

            // Check if click is within wall's horizontal range
            if (modelPos >= wallStart - 20 && modelPos <= wallEnd + 20) {
              const wallHeight = wall.height || 96;
              const wallTop = floorElev + wallHeight;
              const wallBottom = floorElev;

              // Check if click height is within this wall's vertical range
              if (clickHeightFromGrade >= wallBottom - 10 && clickHeightFromGrade <= wallTop + 10) {
                // Found a matching wall
                const pos = Math.max(0.05, Math.min(0.95, (modelPos - wallStart) / (wallEnd - wallStart)));

                // Prefer walls that are closer to the click height
                const heightDiff = Math.abs(clickHeightFromGrade - (floorElev + wallHeight / 2));
                if (!bestMatch || heightDiff < bestMatch.heightDiff) {
                  bestMatch = {
                    wall,
                    floor,
                    position: pos,
                    heightDiff,
                    floorElev,
                  };
                }
              }
            }
          }
        }

        if (bestMatch) {
          targetWall = bestMatch.wall;
          targetFloor = bestMatch.floor;
          wallPosition = bestMatch.position;
          const floorElev = bestMatch.floorElev;

          // Calculate height from THIS floor (not grade)
          const heightFromFloor = clickHeightFromGrade - floorElev;

          if (placementMode === 'window') {
            // Default window size: 36" wide, 48" tall
            const windowWidth = 36;
            const windowHeight = 48;
            // Sill height is where the bottom of window sits (from floor)
            // Center the window on the click point vertically
            const sillHeight = Math.max(24, Math.min(heightFromFloor - windowHeight / 2, 60));

            if (onAddWindow) {
              // Switch to the target floor before adding
              if (targetFloor && onSetActiveFloorId && targetFloor.id !== activeFloorId) {
                onSetActiveFloorId(targetFloor.id);
              }
              onAddWindow({
                wallId: targetWall.id,
                position: wallPosition,
                width: windowWidth,
                height: windowHeight,
                sillHeight: Math.round(sillHeight),
              });
            }
          } else if (placementMode === 'door') {
            // Default door size: 36" wide, 84" tall (sits on floor)
            const doorWidth = 36;
            const doorHeight = 84;

            if (onAddDoor) {
              // Switch to the target floor before adding
              if (targetFloor && onSetActiveFloorId && targetFloor.id !== activeFloorId) {
                onSetActiveFloorId(targetFloor.id);
              }
              onAddDoor({
                wallId: targetWall.id,
                position: wallPosition,
                width: doorWidth,
                height: doorHeight,
                swing: 'left',
                type: 'single',
              });
            }
          }

          // Exit placement mode after placing
          setPlacementMode(null);
          setPlacementPreview(null);
        }
      }
      return;
    }

    // Don't trigger click if we just finished dragging a grip
    const hitGrip = checkGripPointHit(x, y);
    if (hitGrip) return;

    // Check for post click first - posts stand in front of everything else
    const hitPost = checkPostHit(x, y);
    if (hitPost) {
      setSelectedPostId(hitPost.id);
      setSelectedItemFloorId(hitPost.floorId);
      setSelectedRoofId(null);
      setSelectedLevelId(null);
      setSelectedDimensionId(null);
      setSelectedWindowId(null);
      setSelectedDoorId(null);
      return;
    }

    // Check for window/door click first (they're on top of walls)
    const hitWindowDoor = checkWindowDoorHit(x, y);
    if (hitWindowDoor) {
      if (hitWindowDoor.type === 'window') {
        setSelectedWindowId(hitWindowDoor.id);
        setSelectedDoorId(null);
        setSelectedItemFloorId(hitWindowDoor.floorId);
      } else {
        setSelectedDoorId(hitWindowDoor.id);
        setSelectedWindowId(null);
        setSelectedItemFloorId(hitWindowDoor.floorId);
      }
      setSelectedRoofId(null);
      setSelectedLevelId(null);
      setSelectedDimensionId(null);
      setSelectedPostId(null);
      return;
    }

    // Check for dimension click
    const hitDimension = checkDimensionHit(x, y);
    if (hitDimension) {
      setSelectedDimensionId(hitDimension.id);
      setSelectedRoofId(null);
      setSelectedPostId(null);
      setSelectedLevelId(null);
      setSelectedWindowId(null);
      setSelectedDoorId(null);
      return;
    }

    // Check for roof click
    const hitRoof = checkRoofHit(x, y);
    if (hitRoof) {
      setSelectedRoofId(hitRoof.id);
      setSelectedLevelId(null);
      setSelectedPostId(null);
      setSelectedDimensionId(null);
      setSelectedWindowId(null);
      setSelectedDoorId(null);
      setEditingRoof(true);
      return;
    }

    // Check for level line click
    const hitLevel = checkLevelLineHit(x, y);
    if (hitLevel) {
      setSelectedLevelId(hitLevel.id);
      setSelectedRoofId(null);
      setSelectedPostId(null);
      setSelectedDimensionId(null);
      setSelectedWindowId(null);
      setSelectedDoorId(null);
      // Get current wall height
      const currentHeight = activeFloor?.walls?.[0]?.height || 96;
      setWallHeightInput(String(Math.round(currentHeight)));
      setEditingWallHeight(true);
    } else {
      // Clicked on nothing - clear selection
      setSelectedLevelId(null);
      setSelectedRoofId(null);
      setSelectedDimensionId(null);
      setSelectedWindowId(null);
      setSelectedDoorId(null);
      setSelectedPostId(null);
    }
  }, [measureMode, dimensionMode, draggingLevelGrip, draggingWindowDoor, editingWallHeight, editingRoof, screenToCanvas, activeFloor, floors, activeFloorId, onSetActiveFloorId, elevationDimensions, activeElevation, placementMode, renderInfo, onAddWindow, onAddDoor, checkWindowDoorHit]);

  const handleMouseMove = (e) => {
    const { x, y } = screenToCanvas(e.clientX, e.clientY);

    // Handle grip point dragging
    if (draggingLevelGrip) {
      const deltaX = x - draggingLevelGrip.startX;
      let newExtension = draggingLevelGrip.startExtension + deltaX;

      // Apply snapping
      newExtension = snapExtension(newExtension, draggingLevelGrip.id);

      setLevelLineExtensions(prev => ({
        ...prev,
        [draggingLevelGrip.id]: newExtension,
      }));
      return;
    }

    // Handle window/door dragging
    if (draggingWindowDoor && renderInfo) {
      const { elevScale, offsetX, floorY, flipX, gradeY, elevationWalls } = renderInfo;
    // Openings are drawn from the roof-expanded origin, so hit-test against that
    const minPos = renderInfo.modelMinPos ?? renderInfo.minPos;
    const maxPos = renderInfo.modelMaxPos ?? renderInfo.maxPos;
      const isHorizontal = activeElevation === 'north' || activeElevation === 'south';
      // GRID_SIZE imported from constants/grid

      // Calculate delta in screen space
      const deltaX = x - draggingWindowDoor.startX;
      const deltaY = y - draggingWindowDoor.startY;

      // Find the wall for this window/door
      const floor = floors.find(f => f.id === draggingWindowDoor.floorId) || activeFloor;
      let item, wall;
      if (draggingWindowDoor.type === 'window') {
        item = floor?.windows?.find(w => w.id === draggingWindowDoor.id);
        wall = item ? floor?.walls?.find(w => w.id === item.wallId) : null;
      } else {
        item = floor?.doors?.find(d => d.id === draggingWindowDoor.id);
        wall = item ? floor?.walls?.find(w => w.id === item.wallId) : null;
      }

      if (wall && item) {
        // Calculate wall direction (signed - can be positive or negative depending on wall orientation)
        // This is important because position 0→1 follows wall.start→wall.end, not min→max
        const wallDiff = isHorizontal
          ? (wall.end.x - wall.start.x)
          : (wall.end.y - wall.start.y);

        if (Math.abs(wallDiff) < 1) return; // Avoid division by near-zero

        // Convert deltaX to model position change
        // For non-flipped: positive deltaX = move right = increase model X
        // For flipped: positive deltaX = move right = DECREASE model X (view is mirrored)
        const modelDelta = flipX
          ? -deltaX / ((6 / GRID_SIZE) * elevScale)
          : deltaX / ((6 / GRID_SIZE) * elevScale);

        // Calculate new position (0-1 along wall)
        // Using wallDiff (signed) instead of wallLength (always positive) to handle
        // walls that run in either direction (start→end can be left-to-right or right-to-left)
        const positionDelta = modelDelta / wallDiff;
        let newPosition = draggingWindowDoor.originalPosition + positionDelta;
        newPosition = Math.max(0.05, Math.min(0.95, newPosition)); // Clamp

        // Update window/door position (horizontal only - sill height edited via properties panel)
        if (draggingWindowDoor.type === 'window' && onUpdateWindow) {
          onUpdateWindow(draggingWindowDoor.floorId, draggingWindowDoor.id, {
            position: newPosition,
          });
        } else if (draggingWindowDoor.type === 'door' && onUpdateDoor) {
          onUpdateDoor(draggingWindowDoor.floorId, draggingWindowDoor.id, {
            position: newPosition,
          });
        }
      }
      return;
    }

    // Update hovered grip point for cursor change
    const hitGrip = checkGripPointHit(x, y);
    setHoveredGrip(hitGrip ? hitGrip.id : null);

    if (dimensionMode && dimensionStart) {
      // Apply snapping to dimension points
      const snappedPos = getSnappedPosition(x, y);
      setDimensionPreview(snappedPos);
      return;
    }

    if (measureMode && measureStart) {
      setMeasureEnd({ x, y });
    } else {
      handlePanMove(e.clientX, e.clientY);
    }
  };

  const handleMouseUp = () => {
    // Stop grip dragging
    if (draggingLevelGrip) {
      setDraggingLevelGrip(null);
      return;
    }

    // Stop window/door dragging
    if (draggingWindowDoor) {
      setDraggingWindowDoor(null);
      return;
    }

    // Create dimension when releasing in dimension mode
    if (dimensionMode && dimensionStart && dimensionPreview && renderInfo) {
      const dx = dimensionPreview.x - dimensionStart.x;
      const dy = dimensionPreview.y - dimensionStart.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Only create if dragged a meaningful distance (at least 10 pixels)
      if (distance > 10) {
        // Convert screen coordinates to model coordinates for storage
        const startModel = screenToModel(dimensionStart.x, dimensionStart.y);
        const endModel = screenToModel(dimensionPreview.x, dimensionPreview.y);

        if (startModel && endModel) {
          const newDimension = {
            id: `dim-${Date.now()}`,
            start: startModel,
            end: endModel,
            offset: 30, // Default offset from the line (pixels - stays constant)
          };

          setElevationDimensions(prev => ({
            ...prev,
            [activeElevation]: [...(prev[activeElevation] || []), newDimension],
          }));
        }
      }

      setDimensionStart(null);
      setDimensionPreview(null);
      return;
    }

    if (measureMode && measureStart && measureEnd) {
      // Keep the measurement visible
    } else {
      handlePanEnd();
    }
  };

  // Mouse wheel zoom handler
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.1, Math.min(4, prev + delta)));
  }, []);

  // Touch handlers - track touch start position to detect taps vs pans
  const touchStartRef = useRef(null);
  const touchMovedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const touchStartHandler = (e) => {
      if (e.touches.length === 1) {
        // Don't intercept touches on interactive elements (buttons, inputs, panels)
        const target = e.target;
        const isInteractive = target.tagName === 'BUTTON' ||
                              target.tagName === 'INPUT' ||
                              target.tagName === 'SELECT' ||
                              target.tagName === 'TEXTAREA' ||
                              target.closest('[data-panel]') ||
                              target.closest('button') ||
                              target.closest('input') ||
                              target.closest('select');

        if (isInteractive) {
          // Let the touch through to the interactive element
          return;
        }

        e.preventDefault();
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
        touchMovedRef.current = false;
        handlePanStart(touch.clientX, touch.clientY);
      }
    };

    const touchMoveHandler = (e) => {
      if (e.touches.length === 1) {
        // Only handle if we started tracking a touch (not on interactive element)
        if (!touchStartRef.current) return;

        e.preventDefault();
        const touch = e.touches[0];
        // Check if moved more than 10px - consider it a pan, not a tap
        const dx = touch.clientX - touchStartRef.current.x;
        const dy = touch.clientY - touchStartRef.current.y;
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          touchMovedRef.current = true;
        }
        setPan({
          x: touch.clientX - panStart.x,
          y: touch.clientY - panStart.y,
        });
      }
    };

    const touchEndHandler = (e) => {
      // Update ref first so handleCanvasClick sees the correct value
      isPanningRef.current = false;
      setIsPanning(false);
      // If touch didn't move much and was quick, treat as a tap
      if (touchStartRef.current && !touchMovedRef.current) {
        const elapsed = Date.now() - touchStartRef.current.time;
        if (elapsed < 300) {
          // Simulate a click event for level line/roof selection
          const syntheticEvent = {
            clientX: touchStartRef.current.x,
            clientY: touchStartRef.current.y,
          };
          handleCanvasClick(syntheticEvent);
        }
      }
      touchStartRef.current = null;
    };

    container.addEventListener('touchstart', touchStartHandler, { passive: false });
    container.addEventListener('touchmove', touchMoveHandler, { passive: false });
    container.addEventListener('touchend', touchEndHandler, { passive: false });

    return () => {
      container.removeEventListener('touchstart', touchStartHandler);
      container.removeEventListener('touchmove', touchMoveHandler);
      container.removeEventListener('touchend', touchEndHandler);
    };
  }, [handlePanStart, panStart, handleCanvasClick]);

  // Clear measurement when toggling measure mode off
  useEffect(() => {
    if (!measureMode) {
      setMeasureStart(null);
      setMeasureEnd(null);
    }
  }, [measureMode]);

  // Handle keyboard delete for selected dimensions
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedDimensionId) {
        // Don't delete if we're in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        e.preventDefault();
        setElevationDimensions(prev => ({
          ...prev,
          [activeElevation]: (prev[activeElevation] || []).filter(d => d.id !== selectedDimensionId),
        }));
        setSelectedDimensionId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedDimensionId, activeElevation]);

  // Render elevation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate render scale for crisp rendering at any zoom level
    // Use device pixel ratio plus zoom factor for high-DPI displays
    const dpr = window.devicePixelRatio || 1;
    const renderScale = Math.max(1, zoom) * dpr;

    // Set canvas resolution (internal pixels)
    const renderWidth = Math.round(canvasSize.width * renderScale);
    const renderHeight = Math.round(canvasSize.height * renderScale);

    canvas.width = renderWidth;
    canvas.height = renderHeight;

    // Scale the context to match the render scale
    ctx.scale(renderScale, renderScale);

    // Clear canvas
    ctx.fillStyle = renderMode === 'color' ? '#f5f5f5' : '#ffffff';
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

    // Build options for drawElevation
    const options = {
      renderMode,
      sidingPattern,
      detailedOpenings: true,
      clipPatterns: true,
      showDimensions,
      marginX: 60,
      marginY: 80,
      foundationHeightInches: foundationHeight,
      showFoundation: foundationHeight > 0,
      showNoWallsMessage: true,
      useFixedScale: selectedScale !== 'auto',
      selectedRoofId,
      selectedPostId,
      selectedLevelId,
      showLevelLines,
      levelLineExtensions,
      showGripPoints: true, // Always show grip points in ElevationView
    };

    // Convert selectedScale to the format expected by drawElevation
    // drawElevation uses keys like '1/8" = 1\'' in its ELEVATION_SCALE_MAP
    let scale = null;
    if (selectedScale !== 'auto') {
      const scaleStringMap = {
        '1/16': '1/16" = 1\'',
        '1/8': '1/8" = 1\'',
        '3/16': '3/16" = 1\'',
        '1/4': '1/4" = 1\'',
        '3/8': '3/8" = 1\'',
        '1/2': '1/2" = 1\'',
        '3/4': '3/4" = 1\'',
        '1': '1" = 1\'',
      };
      scale = scaleStringMap[selectedScale] || null;
    }

    // Get floor data - combine all floors for stacked elevation view
    // Each element gets a floorElevation property indicating its vertical offset
    let combinedWalls = [];
    let combinedDoors = [];
    let combinedWindows = [];
    let combinedRoofs = [];
    let combinedPosts = [];

    // Use all floors if available, otherwise just active floor
    const floorsToRender = floors.length > 0 ? floors : (activeFloor ? [activeFloor] : []);

    floorsToRender.forEach(floor => {
      const floorElevation = floor.floorElevation || 0; // Height from grade in inches

      // Add walls with floor elevation offset
      (floor.walls || []).forEach(wall => {
        combinedWalls.push({
          ...wall,
          floorElevation,
          floorId: floor.id,
          floorName: floor.name,
        });
      });

      // Add doors with floor elevation offset
      (floor.doors || []).forEach(door => {
        combinedDoors.push({
          ...door,
          floorElevation,
          floorId: floor.id,
        });
      });

      // Add windows with floor elevation offset
      (floor.windows || []).forEach(window => {
        combinedWindows.push({
          ...window,
          floorElevation,
          floorId: floor.id,
        });
      });

      // Add roofs with floor elevation offset (roofs typically on highest floor)
      (floor.roofs || []).forEach(roof => {
        combinedRoofs.push({
          ...roof,
          floorElevation,
          floorId: floor.id,
        });
      });

      // Add posts with floor elevation offset
      (floor.posts || []).forEach(post => {
        combinedPosts.push({
          ...post,
          floorElevation,
          floorId: floor.id,
        });
      });
    });

    // Build bounds object - use logical size (before render scale)
    const bounds = {
      x: 0,
      y: 0,
      width: canvasSize.width,
      height: canvasSize.height,
      scale: scale,
    };

    // Build data object with combined floor data
    const data = {
      walls: combinedWalls,
      doors: combinedDoors,
      windows: combinedWindows,
      roofs: combinedRoofs,
      posts: combinedPosts,
      floors: floorsToRender, // Pass floor info for level lines
    };

    // Call the shared drawElevation utility
    // Signature: drawElevation(ctx, bounds, data, elevation, options)
    const info = drawElevation(ctx, bounds, data, activeElevation, options);

    // Adjust render info scales for the render multiplier
    if (info) {
      info.renderScale = renderScale;
    }
    setRenderInfo(info);

    // Draw selection highlight for selected window/door
    if (info && (selectedWindowId || selectedDoorId)) {
      const { elevScale, offsetX, floorY, flipX, elevationWalls } = info;
      const minPos = info.modelMinPos ?? info.minPos;
      const maxPos = info.modelMaxPos ?? info.maxPos;
      const isHorizontal = activeElevation === 'north' || activeElevation === 'south';
      // GRID_SIZE imported from constants/grid

      // Get visible wall IDs to only highlight items on walls visible in this elevation
      const visibleWallIds = new Set((elevationWalls || []).map(w => w.id));

      const floorsToSearch = floors.length > 0 ? floors : (activeFloor ? [activeFloor] : []);

      for (const floor of floorsToSearch) {
        const floorElev = floor.floorElevation || 0;
        const walls = floor.walls || [];

        if (selectedWindowId) {
          const win = (floor.windows || []).find(w => w.id === selectedWindowId);
          if (win) {
            const wall = walls.find(w => w.id === win.wallId);
            if (wall && visibleWallIds.has(wall.id)) {
              let winPos;
              if (isHorizontal) {
                winPos = wall.start.x + (wall.end.x - wall.start.x) * win.position;
              } else {
                winPos = wall.start.y + (wall.end.y - wall.start.y) * win.position;
              }

              let winCenterX;
              if (flipX) {
                winCenterX = offsetX + (maxPos - winPos) * (6 / GRID_SIZE) * elevScale;
              } else {
                winCenterX = offsetX + (winPos - minPos) * (6 / GRID_SIZE) * elevScale;
              }

              const winW = (win.width || 36) * elevScale;
              const winH = (win.height || 48) * elevScale;
              const winSill = (win.sillHeight || 36) * elevScale;
              const winFloorOffset = floorElev * elevScale;
              const winY = floorY - winSill - winH - winFloorOffset;
              const winX = winCenterX - winW / 2;

              ctx.save();
              ctx.strokeStyle = '#00ffaa';
              ctx.lineWidth = 3;
              ctx.setLineDash([5, 3]);
              ctx.strokeRect(winX - 4, winY - 4, winW + 8, winH + 8);
              ctx.restore();
            }
          }
        }

        if (selectedDoorId) {
          const door = (floor.doors || []).find(d => d.id === selectedDoorId);
          if (door) {
            const wall = walls.find(w => w.id === door.wallId);
            if (wall && visibleWallIds.has(wall.id)) {
              let doorPos;
              if (isHorizontal) {
                doorPos = wall.start.x + (wall.end.x - wall.start.x) * door.position;
              } else {
                doorPos = wall.start.y + (wall.end.y - wall.start.y) * door.position;
              }

              let doorX;
              if (flipX) {
                doorX = offsetX + (maxPos - doorPos) * (6 / GRID_SIZE) * elevScale;
              } else {
                doorX = offsetX + (doorPos - minPos) * (6 / GRID_SIZE) * elevScale;
              }

              const doorW = (door.width || 36) * elevScale;
              const doorH = (door.height || 84) * elevScale;
              const doorFloorOffset = floorElev * elevScale;
              const doorY = floorY - doorH - doorFloorOffset;

              ctx.save();
              ctx.strokeStyle = '#00ffaa';
              ctx.lineWidth = 3;
              ctx.setLineDash([5, 3]);
              ctx.strokeRect(doorX - doorW/2 - 4, doorY - 4, doorW + 8, doorH + 8);
              ctx.restore();
            }
          }
        }
      }
    }

    // Draw measurement line if in measure mode
    if (measureMode && measureStart && measureEnd && info) {
      const { elevScale } = info;

      ctx.save();
      ctx.strokeStyle = '#ff6600';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);

      ctx.beginPath();
      ctx.moveTo(measureStart.x, measureStart.y);
      ctx.lineTo(measureEnd.x, measureEnd.y);
      ctx.stroke();

      // Calculate distance in inches
      const dx = measureEnd.x - measureStart.x;
      const dy = measureEnd.y - measureStart.y;
      const pixelDist = Math.sqrt(dx * dx + dy * dy);
      const inchDist = pixelDist / elevScale;

      // Format as feet-inches
      const feet = Math.floor(inchDist / 12);
      const inches = Math.round(inchDist % 12);
      const distLabel = feet > 0 ? `${feet}' - ${inches}"` : `${inches}"`;

      // Draw label
      const midX = (measureStart.x + measureEnd.x) / 2;
      const midY = (measureStart.y + measureEnd.y) / 2;

      ctx.setLineDash([]);
      ctx.fillStyle = '#ff6600';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      // Background for label
      const labelWidth = ctx.measureText(distLabel).width + 10;
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillRect(midX - labelWidth/2, midY - 20, labelWidth, 18);

      ctx.fillStyle = '#ff6600';
      ctx.fillText(distLabel, midX, midY - 5);

      ctx.restore();
    }

    // Helper function to draw a dimension line
    const drawDimension = (ctx, start, end, elevScale, isPreview = false, isSelected = false, offset = 30) => {
      ctx.save();

      // Calculate dimension line direction and length
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length < 5) {
        ctx.restore();
        return;
      }

      // Calculate perpendicular offset direction
      const perpX = -dy / length;
      const perpY = dx / length;

      // Offset the dimension line from the measured points
      const dimStart = { x: start.x + perpX * offset, y: start.y + perpY * offset };
      const dimEnd = { x: end.x + perpX * offset, y: end.y + perpY * offset };

      // Extension line length (from point to dimension line)
      const extLen = offset + 5;

      // Colors
      const color = isPreview ? '#00c8ff' : isSelected ? '#ff6600' : '#333';
      const lineWidth = isSelected ? 2 : 1;

      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.setLineDash([]);

      // Draw extension lines
      ctx.beginPath();
      ctx.moveTo(start.x + perpX * 5, start.y + perpY * 5);
      ctx.lineTo(start.x + perpX * extLen, start.y + perpY * extLen);
      ctx.moveTo(end.x + perpX * 5, end.y + perpY * 5);
      ctx.lineTo(end.x + perpX * extLen, end.y + perpY * extLen);
      ctx.stroke();

      // Draw dimension line
      ctx.beginPath();
      ctx.moveTo(dimStart.x, dimStart.y);
      ctx.lineTo(dimEnd.x, dimEnd.y);
      ctx.stroke();

      // Draw tick marks at ends (perpendicular to dimension line)
      const tickLen = 6;
      const tickDx = (dx / length) * tickLen;
      const tickDy = (dy / length) * tickLen;

      ctx.beginPath();
      // Start tick (angled)
      ctx.moveTo(dimStart.x - tickDx + perpX * tickLen, dimStart.y - tickDy + perpY * tickLen);
      ctx.lineTo(dimStart.x + tickDx - perpX * tickLen, dimStart.y + tickDy - perpY * tickLen);
      // End tick (angled)
      ctx.moveTo(dimEnd.x - tickDx + perpX * tickLen, dimEnd.y - tickDy + perpY * tickLen);
      ctx.lineTo(dimEnd.x + tickDx - perpX * tickLen, dimEnd.y + tickDy - perpY * tickLen);
      ctx.stroke();

      // Calculate and draw measurement text
      const inchDist = length / elevScale;
      const feet = Math.floor(inchDist / 12);
      const inches = Math.round(inchDist % 12);
      const distLabel = feet > 0 ? `${feet}'-${inches}"` : `${inches}"`;

      // Position text at midpoint of dimension line
      const midX = (dimStart.x + dimEnd.x) / 2;
      const midY = (dimStart.y + dimEnd.y) / 2;

      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Draw text background
      const textWidth = ctx.measureText(distLabel).width + 8;
      const textHeight = 16;

      // Calculate rotation angle for text
      const angle = Math.atan2(dy, dx);
      // Keep text readable (not upside down)
      const adjustedAngle = (angle > Math.PI / 2 || angle < -Math.PI / 2)
        ? angle + Math.PI
        : angle;

      ctx.save();
      ctx.translate(midX, midY);
      ctx.rotate(adjustedAngle);

      // White background behind text
      ctx.fillStyle = '#fff';
      ctx.fillRect(-textWidth / 2, -textHeight / 2, textWidth, textHeight);

      // Draw text
      ctx.fillStyle = color;
      ctx.fillText(distLabel, 0, 0);

      ctx.restore();
      ctx.restore();
    };

    // Draw snap points when in dimension mode
    if (dimensionMode && info) {
      const { elevScale, offsetX, gradeY, roofBaseY, groundY, elevWidth, drawRoofPeak, drawOverhang } = info;
      ctx.save();

      // Generate snap points (same logic as getSnapPoints but directly using info)
      const snapPoints = [];
      const leftX = offsetX;
      const rightX = offsetX + elevWidth;

      // Grade level corners
      snapPoints.push({ x: leftX, y: gradeY });
      snapPoints.push({ x: rightX, y: gradeY });

      // Ground level corners
      if (groundY && groundY !== gradeY) {
        snapPoints.push({ x: leftX, y: groundY });
        snapPoints.push({ x: rightX, y: groundY });
      }

      // Eave level corners and ridge
      if (roofBaseY) {
        snapPoints.push({ x: leftX, y: roofBaseY });
        snapPoints.push({ x: rightX, y: roofBaseY });
        if (drawRoofPeak) {
          const peakY = roofBaseY - drawRoofPeak;
          const centerX = offsetX + elevWidth / 2;
          snapPoints.push({ x: centerX, y: peakY });
          if (drawOverhang) {
            snapPoints.push({ x: leftX - drawOverhang, y: roofBaseY });
            snapPoints.push({ x: rightX + drawOverhang, y: roofBaseY });
          }
        }
      }

      // Level line snap points
      if (info.levelBounds) {
        info.levelBounds.forEach(level => {
          snapPoints.push({ x: leftX, y: level.bounds.y });
          snapPoints.push({ x: rightX, y: level.bounds.y });
        });
      }

      // Draw snap indicators
      snapPoints.forEach(sp => {
        ctx.strokeStyle = 'rgba(0, 200, 255, 0.6)';
        ctx.fillStyle = 'rgba(0, 200, 255, 0.3)';
        ctx.lineWidth = 1;

        // Draw small cross at snap point
        const size = 6;
        ctx.beginPath();
        ctx.moveTo(sp.x - size, sp.y);
        ctx.lineTo(sp.x + size, sp.y);
        ctx.moveTo(sp.x, sp.y - size);
        ctx.lineTo(sp.x, sp.y + size);
        ctx.stroke();

        // Draw small diamond
        ctx.beginPath();
        ctx.moveTo(sp.x, sp.y - size);
        ctx.lineTo(sp.x + size, sp.y);
        ctx.lineTo(sp.x, sp.y + size);
        ctx.lineTo(sp.x - size, sp.y);
        ctx.closePath();
        ctx.fill();
      });

      ctx.restore();
    }

    // Draw dimension preview while dragging (preview uses screen coords)
    if (dimensionMode && dimensionStart && dimensionPreview && info) {
      drawDimension(ctx, dimensionStart, dimensionPreview, info.elevScale, true);
    }

    // Draw saved dimensions for this elevation (stored in model coords, need to convert to screen)
    const currentDimensions = elevationDimensions[activeElevation] || [];
    if (currentDimensions.length > 0 && info) {
      const { elevScale, offsetX, gradeY } = info;
      currentDimensions.forEach(dim => {
        // Convert model coords to screen coords
        const startScreen = {
          x: offsetX + dim.start.x * elevScale,
          y: gradeY - dim.start.y * elevScale,
        };
        const endScreen = {
          x: offsetX + dim.end.x * elevScale,
          y: gradeY - dim.end.y * elevScale,
        };
        const isSelected = dim.id === selectedDimensionId;
        drawDimension(ctx, startScreen, endScreen, info.elevScale, false, isSelected, dim.offset);
      });
    }

    // Draw title at bottom
    ctx.fillStyle = '#333';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${activeElevation.toUpperCase()} ELEVATION`, canvas.width / 2, canvas.height - 15);

  }, [activeFloor, floors, activeElevation, renderMode, sidingPattern, foundationHeight,
      showDimensions, selectedScale, showLevelLines, selectedRoofId, selectedLevelId,
      measureMode, measureStart, measureEnd, zoom, canvasSize, levelLineExtensions,
      dimensionMode, dimensionStart, dimensionPreview, elevationDimensions, selectedDimensionId,
      selectedWindowId, selectedDoorId]);

  // Select style for dropdowns - using dark options for browser compatibility
  const selectStyle = {
    padding: '6px 8px',
    background: '#1a1f24',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '11px',
    cursor: 'pointer',
    outline: 'none',
  };

  // Option style for dropdowns (rendered inline via CSS)
  const optionStyle = {
    background: '#1a1f24',
    color: '#fff',
  };

  const labelStyle = {
    color: '#6080a0',
    fontSize: '10px',
    marginRight: '4px',
  };

  const buttonStyle = (active) => ({
    padding: isMobile ? '10px 12px' : '6px 10px',
    background: active ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${active ? 'rgba(0,200,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: '4px',
    color: active ? '#00c8ff' : '#6080a0',
    fontSize: isMobile ? '11px' : '10px',
    cursor: 'pointer',
    fontWeight: active ? '600' : '400',
  });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#080c10',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          background: 'rgba(8,12,16,0.98)',
          borderBottom: '1px solid rgba(0,200,255,0.2)',
          padding: isMobile ? '8px 10px' : '12px 16px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '8px' : '12px',
          flexShrink: 0,
          zIndex: 10,
        }}
      >
        {/* Top row: back button and direction buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <Button variant="default" onClick={onClose}>
            ← {isMobile ? 'Back' : 'Back to Model'}
          </Button>

          {/* Direction buttons */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {['north', 'south', 'east', 'west'].map(dir => (
              <button
                key={dir}
                onClick={() => setActiveElevation(dir)}
                style={buttonStyle(activeElevation === dir)}
              >
                {isMobile ? dir.charAt(0).toUpperCase() : dir.charAt(0).toUpperCase() + dir.slice(1)}
              </button>
            ))}
          </div>

          {/* Render mode buttons - abbreviated on mobile */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { id: 'color', label: 'Color', mobileLabel: 'Col' },
              { id: 'line', label: 'Line', mobileLabel: 'Lin' },
              { id: 'sketchy', label: 'Sketch', mobileLabel: 'Sk' },
            ].map(mode => (
              <button
                key={mode.id}
                onClick={() => setRenderMode(mode.id)}
                style={buttonStyle(renderMode === mode.id)}
              >
                {isMobile ? mode.mobileLabel : mode.label}
              </button>
            ))}
          </div>

          {/* Toggle buttons - compact on mobile */}
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setShowDimensions(!showDimensions)}
              style={{ ...buttonStyle(showDimensions), display: 'flex', alignItems: 'center', gap: '4px' }}
              title="Toggle Auto Dimensions"
            >
              <DimsIcon color={showDimensions ? '#00c8ff' : '#6080a0'} size={16} />
              {!isMobile && 'Dims'}
            </button>
            <button
              onClick={() => {
                const newMode = !measureMode;
                setMeasureMode(newMode);
                if (newMode) {
                  setDimensionMode(false);
                  setDimensionStart(null);
                  setDimensionPreview(null);
                }
              }}
              style={{ ...buttonStyle(measureMode), display: 'flex', alignItems: 'center', gap: '4px' }}
              title="Measure Tool"
            >
              <MeasureIcon color={measureMode ? '#00c8ff' : '#6080a0'} size={16} />
              {!isMobile && 'Measure'}
            </button>
            <button
              onClick={() => {
                const newMode = !dimensionMode;
                setDimensionMode(newMode);
                if (newMode) {
                  setMeasureMode(false);
                  setMeasureStart(null);
                  setMeasureEnd(null);
                } else {
                  // Clear preview when exiting dimension mode
                  setDimensionStart(null);
                  setDimensionPreview(null);
                }
              }}
              style={{ ...buttonStyle(dimensionMode), display: 'flex', alignItems: 'center', gap: '4px' }}
              title="Add Dimension"
            >
              <AddDimensionIcon color={dimensionMode ? '#00c8ff' : '#6080a0'} size={16} />
              {!isMobile && 'Dimension'}
            </button>
            <button
              onClick={() => setShowLevelLines(!showLevelLines)}
              style={{ ...buttonStyle(showLevelLines), display: 'flex', alignItems: 'center', gap: '4px' }}
              title="Level Lines"
            >
              <LevelsIcon color={showLevelLines ? '#00c8ff' : '#6080a0'} size={16} />
              {!isMobile && 'Levels'}
            </button>
            {/* Floors panel button */}
            {floors.length > 0 && (
              <button
                onClick={() => setShowFloorsPanel(!showFloorsPanel)}
                style={{ ...buttonStyle(showFloorsPanel), display: 'flex', alignItems: 'center', gap: '4px' }}
                title="Floor Heights"
              >
                <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke={showFloorsPanel ? '#00c8ff' : '#6080a0'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="6" rx="1" />
                  <rect x="3" y="11" width="18" height="6" rx="1" />
                  <line x1="7" y1="19" x2="17" y2="19" />
                </svg>
                {!isMobile && `Floors (${floors.length})`}
              </button>
            )}

            {/* Placement mode buttons for adding windows/doors */}
            <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '8px', marginLeft: '4px', display: 'flex', gap: '4px' }}>
              <button
                onClick={() => {
                  const newMode = placementMode === 'window' ? null : 'window';
                  setPlacementMode(newMode);
                  if (newMode) {
                    setMeasureMode(false);
                    setDimensionMode(false);
                  }
                  setPlacementPreview(null);
                }}
                style={{ ...buttonStyle(placementMode === 'window'), display: 'flex', alignItems: 'center', gap: '4px' }}
                title="Add Window (click on wall in elevation)"
              >
                <WindowIcon color={placementMode === 'window' ? '#00c8ff' : '#6080a0'} size={16} />
                {!isMobile && '+Win'}
              </button>
              <button
                onClick={() => {
                  const newMode = placementMode === 'door' ? null : 'door';
                  setPlacementMode(newMode);
                  if (newMode) {
                    setMeasureMode(false);
                    setDimensionMode(false);
                  }
                  setPlacementPreview(null);
                }}
                style={{ ...buttonStyle(placementMode === 'door'), display: 'flex', alignItems: 'center', gap: '4px' }}
                title="Add Door (click on wall in elevation)"
              >
                <DoorIcon color={placementMode === 'door' ? '#00c8ff' : '#6080a0'} size={16} />
                {!isMobile && '+Door'}
              </button>
            </div>
          </div>
        </div>

        {/* Second row: Options - more compact on mobile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
          {/* Siding dropdown */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && <span style={labelStyle}>Siding:</span>}
            <select
              value={sidingPattern}
              onChange={(e) => setSidingPattern(e.target.value)}
              style={selectStyle}
              title="Siding"
            >
              {SIDING_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} style={optionStyle}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Block size dropdown */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && <span style={labelStyle}>Block:</span>}
            <select
              value={foundationHeight}
              onChange={(e) => setFoundationHeight(Number(e.target.value))}
              style={selectStyle}
              title="Block Height"
            >
              {BLOCK_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} style={optionStyle}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Scale dropdown */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && <span style={labelStyle}>Scale:</span>}
            <select
              value={selectedScale}
              onChange={(e) => setSelectedScale(e.target.value)}
              style={selectStyle}
              title="Scale"
            >
              {SCALE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} style={optionStyle}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1 }} />

          {/* Zoom controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => setZoom(z => Math.max(0.1, z - 0.25))}
              style={{
                padding: '4px 8px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              −
            </button>
            <span style={{ color: '#fff', fontSize: '11px', minWidth: '50px', textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom(z => Math.min(3, z + 0.25))}
              style={{
                padding: '4px 8px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              +
            </button>
            <button
              onClick={fitToScreen}
              style={{
                padding: '4px 8px',
                background: 'rgba(0,200,255,0.15)',
                border: '1px solid rgba(0,200,255,0.3)',
                borderRadius: '4px',
                color: '#00c8ff',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: '500',
              }}
            >
              Fit
            </button>
          </div>
        </div>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: 'hidden',
          padding: isMobile ? '10px' : '20px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: draggingWindowDoor ? 'move' : (draggingLevelGrip ? 'ew-resize' : (hoveredGrip ? 'ew-resize' : ((measureMode || dimensionMode) ? 'crosshair' : (isPanning ? 'grabbing' : 'grab')))),
          background: renderMode === 'color' ? '#f5f5f5' : '#ffffff',
          position: 'relative',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onClick={handleCanvasClick}
      >
        <canvas
          key={`canvas-${canvasSize.width}-${canvasSize.height}-${zoom}`}
          ref={canvasRef}
          style={{
            // CSS size controls display size
            width: canvasSize.width * zoom + 'px',
            height: canvasSize.height * zoom + 'px',
            transform: `translate(${pan.x}px, ${pan.y}px)`,
            transformOrigin: 'center center',
            background: renderMode === 'color' ? '#f5f5f5' : '#ffffff',
            flexShrink: 0,
            pointerEvents: 'none',
            // Ensure crisp rendering
            imageRendering: 'auto',
          }}
        />

        {/* Wall Height Editor Popup */}
        {editingWallHeight && (
          <>
            {/* Backdrop to close dialog */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.3)',
                zIndex: 99,
              }}
              onClick={() => setEditingWallHeight(false)}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.stopPropagation();
                setEditingWallHeight(false);
              }}
            />
            <div
              data-panel="true"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(8,12,16,0.95)',
                border: '1px solid rgba(0,200,255,0.4)',
                borderRadius: '8px',
                padding: '16px',
                zIndex: 100,
                minWidth: '200px',
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>
                  Wall Height
                </span>
                <button
                  onClick={() => setEditingWallHeight(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6080a0',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '0 4px',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                value={wallHeightInput}
                onChange={(e) => setWallHeightInput(e.target.value)}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const inches = parseFloat(wallHeightInput);
                    if (!isNaN(inches) && inches > 0) {
                      updateWallHeight(inches);
                    }
                  } else if (e.key === 'Escape') {
                    setEditingWallHeight(false);
                  }
                }}
                autoFocus
              />
              <span style={{ color: '#6080a0', fontSize: '12px' }}>inches</span>
            </div>
            <div style={{ color: '#6080a0', fontSize: '10px', marginTop: '8px' }}>
              {Math.floor(parseFloat(wallHeightInput) / 12)}' - {Math.round(parseFloat(wallHeightInput) % 12)}"
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button
                onClick={() => {
                  const inches = parseFloat(wallHeightInput);
                  if (!isNaN(inches) && inches > 0) {
                    updateWallHeight(inches);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: 'rgba(0,200,255,0.2)',
                  border: '1px solid rgba(0,200,255,0.4)',
                  borderRadius: '4px',
                  color: '#00c8ff',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Apply
              </button>
              <button
                onClick={() => setEditingWallHeight(false)}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  color: '#6080a0',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
          </>
        )}

        {/* Roof Editor Panel */}
        {editingRoof && selectedRoof && (
          <RoofEditor
            roof={selectedRoof}
            onUpdate={handleRoofUpdate}
            onDelete={handleRoofDelete}
            onClose={() => {
              setEditingRoof(false);
              setSelectedRoofId(null);
            }}
            x={20}
            y={100}
            isMobile={isMobile}
          />
        )}

        {selectedPost && (
          <PostEditor
            post={selectedPost}
            onUpdate={handlePostUpdate}
            onDelete={handlePostDelete}
            onClose={() => setSelectedPostId(null)}
            x={20}
            y={100}
            isMobile={isMobile}
          />
        )}

        {/* Window Editor Panel */}
        {selectedWindowId && (() => {
          const floor = floors.find(f => f.id === selectedItemFloorId) || activeFloor;
          const win = floor?.windows?.find(w => w.id === selectedWindowId);
          if (!win) return null;
          // Find the wall this window is on to constrain sill height
          const wall = floor?.walls?.find(w => w.id === win.wallId);
          return (
            <WindowEditor
              window={win}
              wall={wall}
              onUpdate={(updates) => {
                if (onUpdateWindow && selectedItemFloorId) {
                  onUpdateWindow(selectedItemFloorId, selectedWindowId, updates);
                }
              }}
              onDelete={() => {
                if (onDeleteWindow && selectedItemFloorId) {
                  onDeleteWindow(selectedItemFloorId, selectedWindowId);
                  setSelectedWindowId(null);
                  setSelectedItemFloorId(null);
                }
              }}
              onClose={() => {
                setSelectedWindowId(null);
                setSelectedItemFloorId(null);
              }}
              formatMeasurement={formatMeasurement || ((v) => `${v.toFixed(1)}'`)}
              units={units}
              x={isMobile ? 10 : 20}
              y={isMobile ? 140 : 100}
              isMobile={isMobile}
            />
          );
        })()}

        {/* Door Editor Panel */}
        {selectedDoorId && (() => {
          const floor = floors.find(f => f.id === selectedItemFloorId) || activeFloor;
          const door = floor?.doors?.find(d => d.id === selectedDoorId);
          if (!door) return null;
          return (
            <DoorEditor
              door={door}
              onUpdate={(updates) => {
                if (onUpdateDoor && selectedItemFloorId) {
                  onUpdateDoor(selectedItemFloorId, selectedDoorId, updates);
                }
              }}
              onDelete={() => {
                if (onDeleteDoor && selectedItemFloorId) {
                  onDeleteDoor(selectedItemFloorId, selectedDoorId);
                  setSelectedDoorId(null);
                  setSelectedItemFloorId(null);
                }
              }}
              onClose={() => {
                setSelectedDoorId(null);
                setSelectedItemFloorId(null);
              }}
              formatMeasurement={formatMeasurement || ((v) => `${v.toFixed(1)}'`)}
              units={units}
              x={isMobile ? 10 : 20}
              y={isMobile ? 140 : 100}
              isMobile={isMobile}
            />
          );
        })()}

        {/* Floors Management Panel */}
        {showFloorsPanel && floors.length > 0 && (
          <div
            style={{
              position: 'absolute',
              right: 20,
              top: isMobile ? 130 : 90,
              width: isMobile ? 260 : 280,
              background: 'linear-gradient(180deg, rgba(15,20,28,0.98) 0%, rgba(8,12,16,0.98) 100%)',
              border: '1px solid rgba(0,200,255,0.2)',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              zIndex: 1000,
              overflow: 'hidden',
            }}
            data-panel="true"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(0,200,255,0.05)',
            }}>
              <span style={{ color: '#00c8ff', fontWeight: '600', fontSize: '12px' }}>
                Floor Heights
              </span>
              <button
                onClick={() => setShowFloorsPanel(false)}
                style={{
                  background: 'rgba(255,100,100,0.15)',
                  border: 'none',
                  color: '#ff6666',
                  fontSize: '10px',
                  cursor: 'pointer',
                  padding: '3px 6px',
                  borderRadius: '3px',
                }}
              >
                ×
              </button>
            </div>

            {/* Floor list */}
            <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '10px', color: '#6080a0', marginBottom: '4px' }}>
                Set the elevation (height from ground) for each floor. Upper floors should have higher values.
              </div>
              {floors.map((floor, idx) => (
                <div
                  key={floor.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    background: floor.id === activeFloorId ? 'rgba(0,200,255,0.1)' : 'rgba(255,255,255,0.04)',
                    borderRadius: '6px',
                    border: floor.id === activeFloorId ? '1px solid rgba(0,200,255,0.3)' : '1px solid transparent',
                  }}
                >
                  <span style={{
                    flex: 1,
                    color: '#d0d8e0',
                    fontSize: '12px',
                    fontWeight: floor.id === activeFloorId ? '600' : '400',
                  }}>
                    {floor.name}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {editingFloorElevation?.floorId === floor.id ? (
                      <input
                        type="number"
                        value={editingFloorElevation.value}
                        onChange={(e) => setEditingFloorElevation({ floorId: floor.id, value: e.target.value })}
                        onBlur={() => {
                          const newElevation = parseFloat(editingFloorElevation.value) * 12; // Convert feet to inches
                          if (!isNaN(newElevation) && onUpdateFloorElevation) {
                            onUpdateFloorElevation(floor.id, Math.max(0, newElevation));
                          }
                          setEditingFloorElevation(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          } else if (e.key === 'Escape') {
                            setEditingFloorElevation(null);
                          }
                        }}
                        autoFocus
                        style={{
                          width: '50px',
                          padding: '4px 6px',
                          background: '#1a1f24',
                          border: '1px solid rgba(0,200,255,0.4)',
                          borderRadius: '3px',
                          color: '#fff',
                          fontSize: '11px',
                          textAlign: 'right',
                        }}
                      />
                    ) : (
                      <button
                        onClick={() => setEditingFloorElevation({
                          floorId: floor.id,
                          value: Math.round((floor.floorElevation || 0) / 12 * 10) / 10
                        })}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '3px',
                          color: '#fff',
                          fontSize: '11px',
                          cursor: 'pointer',
                          minWidth: '60px',
                          textAlign: 'right',
                        }}
                        title="Click to edit floor elevation"
                      >
                        {Math.round((floor.floorElevation || 0) / 12 * 10) / 10} ft
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Quick presets */}
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: '10px', color: '#6080a0', marginBottom: '6px' }}>Quick Presets:</div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      // Set typical 2-story: ground at 0, upper at 9' (108")
                      if (floors.length >= 2 && onUpdateFloorElevation) {
                        onUpdateFloorElevation(floors[0].id, 0);
                        onUpdateFloorElevation(floors[1].id, 108); // 9 feet
                      }
                    }}
                    style={{
                      padding: '4px 8px',
                      background: 'rgba(0,200,255,0.1)',
                      border: '1px solid rgba(0,200,255,0.3)',
                      borderRadius: '3px',
                      color: '#00c8ff',
                      fontSize: '10px',
                      cursor: 'pointer',
                    }}
                    disabled={floors.length < 2}
                  >
                    2-Story (9' ceil)
                  </button>
                  <button
                    onClick={() => {
                      // Set typical 2-story with 10' ceiling
                      if (floors.length >= 2 && onUpdateFloorElevation) {
                        onUpdateFloorElevation(floors[0].id, 0);
                        onUpdateFloorElevation(floors[1].id, 120); // 10 feet
                      }
                    }}
                    style={{
                      padding: '4px 8px',
                      background: 'rgba(0,200,255,0.1)',
                      border: '1px solid rgba(0,200,255,0.3)',
                      borderRadius: '3px',
                      color: '#00c8ff',
                      fontSize: '10px',
                      cursor: 'pointer',
                    }}
                    disabled={floors.length < 2}
                  >
                    2-Story (10' ceil)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div style={{
        flexShrink: 0,
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#6080a0',
        fontSize: '11px',
        background: 'rgba(8,12,16,0.98)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
      }}>
        <span>Scale: {selectedScale === 'auto' ? 'Auto Fit' : SCALE_OPTIONS.find(s => s.value === selectedScale)?.label}</span>
        {measureMode && <span style={{ color: '#ff6600' }}>Click and drag to measure</span>}
        {dimensionMode && <span style={{ color: '#00c8ff' }}>Click and drag to add dimension</span>}
        {placementMode === 'window' && <span style={{ color: '#00ffaa' }}>Click on wall to place window</span>}
        {placementMode === 'door' && <span style={{ color: '#00ffaa' }}>Click on wall to place door</span>}
        {selectedWindowId && <span style={{ color: '#00ffaa' }}>Window selected - Click elsewhere to deselect</span>}
        {selectedDoorId && <span style={{ color: '#00ffaa' }}>Door selected - Click elsewhere to deselect</span>}
        {selectedDimensionId && !dimensionMode && <span style={{ color: '#00c8ff' }}>Press Delete to remove dimension</span>}
      </div>
    </div>
  );
};

export default ElevationView;
